import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory } from 'react-router-dom';
import { setKanbanLaneOrder, getKanbanLaneOrder } from '../../services/companyKanbanService';
import { Button, TextField, Paper, FormControl, InputLabel, Select } from '@material-ui/core';
import { format } from 'date-fns';
import { useQuery, useQueryClient } from 'react-query';
import { Can } from '../../components/Can';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import KanbanBoard from './KanbanBoard';

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    display: 'flex',
    padding: theme.spacing(1),
    overflowX: 'auto',
    ...theme.scrollbarStyles,
    borderRadius: '10px',
  },
  button: {
    borderRadius: '10px',
  },
  dateInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    marginRight: theme.spacing(1),
  },
  sortSelect: {
    minWidth: 150,
    marginRight: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { user } = useContext(AuthContext);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const queueIds = useMemo(
    () => user?.queues?.map(queue => queue.UserQueue.queueId) || [],
    [user?.queues]
  );

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('sortOrder') || 'ticketNumber';
  });

  const [laneOrder, setLaneOrder] = useState(null);

  const tagsQueryKey = useMemo(
    () => ['kanban', 'tags', user?.companyId],
    [user?.companyId]
  );
  const laneOrderQueryKey = useMemo(
    () => ['kanban', 'lane-order', user?.companyId],
    [user?.companyId]
  );
  const ticketsQueryKey = useMemo(
    () => [
      'kanban',
      'tickets',
      user?.companyId,
      {
        queueIds,
        dateStart: startDate,
        dateEnd: endDate,
      },
    ],
    [user?.companyId, queueIds, startDate, endDate]
  );

  useEffect(() => {
    localStorage.setItem('sortOrder', sortOrder);
  }, [sortOrder]);

  const {
    data: tags = [],
  } = useQuery(
    tagsQueryKey,
    async () => {
      const response = await api.get('/tag/kanban/');
      return response.data.lista || [];
    },
    {
      enabled: !!user?.id,
      staleTime: 0,
      refetchOnWindowFocus: true,
      onError: error => {
        console.log(error);
      },
    }
  );

  const {
    data: savedLaneOrder,
  } = useQuery(
    laneOrderQueryKey,
    getKanbanLaneOrder,
    {
      enabled: !!user?.id,
      staleTime: 0,
      refetchOnWindowFocus: true,
      onError: error => {
        console.error('Erro ao carregar ordem das lanes:', error);
      },
    }
  );

  const {
    data: ticketsResponse,
    refetch: refetchTickets,
  } = useQuery(
    ticketsQueryKey,
    async () => {
      const { data } = await api.get('/ticket/kanban', {
        params: {
          queueIds: JSON.stringify(queueIds),
          dateStart: startDate,
          dateEnd: endDate,
        },
      });
      return data;
    },
    {
      enabled: !!user?.id,
      staleTime: 0,
      refetchInterval: 2000,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      keepPreviousData: true,
      onError: err => {
        console.log(err);
      },
    }
  );

  const tickets = ticketsResponse?.tickets || [];

  useEffect(() => {
    if (savedLaneOrder) {
      setLaneOrder(savedLaneOrder);
    }
  }, [savedLaneOrder]);

  const handleSearchClick = () => {
    refetchTickets();
  };

  const handleStartDateChange = event => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = event => {
    setEndDate(event.target.value);
  };

  const updateTicket = useCallback((updatedTicket) => {
    queryClient.setQueryData(ticketsQueryKey, oldData => {
      if (!oldData?.tickets) return oldData;

      return {
        ...oldData,
        tickets: oldData.tickets.map(ticket =>
          ticket.id === updatedTicket.id ? updatedTicket : ticket
        ),
      };
    });

    refetchTickets();
  }, [queryClient, refetchTickets, ticketsQueryKey]);

  const getOpportunityValue = useCallback((ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return opportunityValue;
  }, []);

  const lanes = useMemo(() => {
    const sortedTickets = [...tickets];

    if (sortOrder === 'ticketNumber') {
      sortedTickets.sort((a, b) => a.id - b.id);
    } else if (sortOrder === 'lastMessageTime') {
      sortedTickets.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    } else if (sortOrder === 'valorDesc') {
      sortedTickets.sort((a, b) => {
        const valorA = getOpportunityValue(a);
        const valorB = getOpportunityValue(b);
        return valorB - valorA;
      });
    }

    const defaultTickets = sortedTickets.filter(
      ticket => (ticket.tags || []).length === 0
    );

    const lanesData = [
      {
        id: 'lane0',
        title: i18n.t('tagsKanban.laneDefault'),
        tickets: defaultTickets,
        color: '#757575',
      },
      ...tags.map(tag => {
        const taggedTickets = sortedTickets.filter(ticket =>
          (ticket.tags || []).some(t => t.id === tag.id)
        );
        return {
          id: tag.id.toString(),
          title: tag.name,
          tickets: taggedTickets,
          color: tag.color || '#757575',
        };
      }),
    ];

    if (laneOrder && laneOrder.length > 0) {
      const orderedLanes = [];
      const laneMap = new Map(lanesData.map(lane => [lane.id, lane]));

      // Adicionar lanes na ordem salva
      laneOrder.forEach(laneId => {
        if (laneMap.has(laneId)) {
          orderedLanes.push(laneMap.get(laneId));
          laneMap.delete(laneId);
        }
      });

      // Adicionar lanes restantes (novas lanes que não estavam na ordem salva)
      laneMap.forEach(lane => orderedLanes.push(lane));

      return orderedLanes;
    }

    return lanesData;
  }, [getOpportunityValue, laneOrder, sortOrder, tags, tickets]);

  const updateTicketLaneCache = useCallback((ticketId, targetLaneId) => {
    queryClient.setQueryData(ticketsQueryKey, oldData => {
      if (!oldData?.tickets) return oldData;

      const kanbanTagIds = new Set(tags.map(tag => tag.id));
      const targetTag = tags.find(tag => tag.id.toString() === targetLaneId);

      return {
        ...oldData,
        tickets: oldData.tickets.map(ticket => {
          if (ticket.id !== ticketId) return ticket;

          const nonKanbanTags = (ticket.tags || []).filter(
            tag => !kanbanTagIds.has(tag.id)
          );

          return {
            ...ticket,
            tags: targetTag
              ? [...nonKanbanTags, targetTag]
              : nonKanbanTags,
          };
        }),
      };
    });
  }, [queryClient, tags, ticketsQueryKey]);

  const handleCardMove = async (ticketId, targetLaneId) => {
    ticketId = parseInt(ticketId, 10);

    updateTicketLaneCache(ticketId, targetLaneId);

    try {
      await api.delete(`/ticket-tags/${ticketId}`);

      if (targetLaneId !== 'lane0') {
        await api.put(`/ticket-tags/${ticketId}/${targetLaneId}`);
        toast.success('Ticket Tag atualizado com sucesso!');
      } else {
        toast.success('Ticket Tag removido!');
      }
    } catch (err) {
      console.log(err);
      toast.error('Erro ao atualizar Ticket Tag');
    } finally {
      await refetchTickets();
    }
  };

  const handleAddColumnClick = () => {
    history.push('/tagsKanban');
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
  };

  const handleLaneReorder = async (sourceIndex, destinationIndex) => {
    // Verificar se o usuário é admin
    if (user.profile !== 'admin') {
      toast.error('Apenas administradores podem reordenar as lanes do Kanban');
      return;
    }

    const newLanes = Array.from(lanes);
    const [reorderedLane] = newLanes.splice(sourceIndex, 1);
    newLanes.splice(destinationIndex, 0, reorderedLane);

    // Salvar nova ordem no banco de dados
    const newLaneOrder = newLanes.map(lane => lane.id);
    setLaneOrder(newLaneOrder);

    try {
      await setKanbanLaneOrder(newLaneOrder);
      queryClient.setQueryData(laneOrderQueryKey, newLaneOrder);
      toast.success('Ordem das lanes atualizada com sucesso');
    } catch (error) {
      console.error('Erro ao salvar ordem das lanes:', error);
      toast.error('Erro ao salvar ordem das lanes');
    } finally {
      queryClient.invalidateQueries(laneOrderQueryKey);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t('Kanban')}</Title>
        <MainHeaderButtonsWrapper>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.sortSelect}
          >
            <InputLabel htmlFor="sort-order-select">Ordenar por</InputLabel>
            <Select
              native
              value={sortOrder}
              onChange={handleSortOrderChange}
              label="Ordenar por"
              inputProps={{
                name: 'sortOrder',
                id: 'sort-order-select',
              }}
            >
              <option value="ticketNumber">Número do Ticket</option>
              <option value="lastMessageTime">Última Mensagem</option>
              <option value="valorDesc">Valor (maior para menor)</option>
            </Select>
          </FormControl>
          <TextField
            label="Data de início"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            className={classes.dateInput}
            size="small"
          />
          <TextField
            label="Data de fim"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            className={classes.dateInput}
            size="small"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearchClick}
            className={classes.button}
          >
            Buscar
          </Button>
          <Can
            role={user.profile}
            perform="dashboard:view"
            yes={() => (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddColumnClick}
                className={classes.button}
              >
                + Adicionar colunas
              </Button>
            )}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper variant="outlined" className={classes.mainPaper}>
        <KanbanBoard
          lanes={lanes}
          onCardMove={handleCardMove}
          onLaneReorder={handleLaneReorder}
          updateTicket={updateTicket}
          isAdmin={user.profile === 'admin'}
        />
      </Paper>
    </MainContainer>
  );
};

export default Kanban;
